import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stAvatarDefaults,
  stCreateAgent,
  stListAgents,
  stSendRoomMessage,
  stUpdateAgent,
  stAgentsConfig,
  StAgent,
} from '../../../services/socialTradingService';
import { SocialTradingAvatarField } from '../SocialTradingAvatarField';
import { I18nFieldBlock } from '../I18nFieldBlock';
import { isLangPackComplete, syncZh } from '../i18nLang';
import {
  StIdCell,
  StActionButton,
  StActionRow,
  StBooleanChip,
  StScrollTable,
  stText,
} from '../StListUi';
import {
  AgentForm,
  CardForm,
  REASONING_TITLE_CN,
  REQUIRED_REASONING_TITLES,
  agentFormFromAgent,
  cardFormFromAgent,
  emptyAgentForm,
  emptyCardForm,
  equityCurveFromAgent,
  numOrUndef,
  percentStrToRatio,
  validateReasoningForm,
} from './helpers';

const EXT_AGENT_SIGNAL = 'XBIT-AGENT-SIGNAL';
const PAGE_SIZE = 20;

function BoundRoomsCell({ agent }: { agent: StAgent }) {
  const rooms = agent.activeRooms || [];
  if (!rooms.length) {
    return <>{agent.activeRoomCount ? String(agent.activeRoomCount) : '—'}</>;
  }
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.25, whiteSpace: 'normal', minWidth: 120 }}>
      {rooms.map((r, i) => (
        <Box component="li" key={r.roomId || r.groupId || `${i}`} sx={{ typography: 'body2' }}>
          {r.name || r.groupId || r.roomId || '—'}
        </Box>
      ))}
    </Box>
  );
}

function profileSourceLabel(source: StAgent['profileSource'], t: (k: string) => string): string {
  if (source === 'LATEST_SIGNAL') return t('module.socialTrading.profileSourceLatestSignal');
  return t('module.socialTrading.profileSourceAdminDefault');
}

export default function SocialTradingAgentsList() {
  const t = useTranslate();
  const notify = useNotify();
  const [agents, setAgents] = useState<StAgent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<AgentForm>(emptyAgentForm());
  const [editAgent, setEditAgent] = useState<StAgent | null>(null);
  const [editForm, setEditForm] = useState<AgentForm>(emptyAgentForm());
  const [cardOpen, setCardOpen] = useState(false);
  const [cardForm, setCardForm] = useState<CardForm>(emptyCardForm());
  const [cardAgent, setCardAgent] = useState<StAgent | null>(null);
  const [strategyOptions, setStrategyOptions] = useState<
    { strategyId: string; agentId: string; label: string }[]
  >([]);
  const [catalogStrategyIds, setCatalogStrategyIds] = useState<string[]>([]);
  const [takenActiveBy, setTakenActiveBy] = useState<Record<string, string>>({});
  const [userDefaultAvatars, setUserDefaultAvatars] = useState<string[]>([]);

  const load = useCallback(async () => {
    try {
      const a = await stListAgents({ activeOnly: false, limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      setAgents(a?.items || []);
      setTotal(a?.total || 0);
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify, page]);

  const loadStrategyOptions = useCallback(async () => {
    try {
      const a = await stListAgents({ activeOnly: false, limit: 200, offset: 0 });
      // Show raw strategyId only (no agent name prefix). Prefer active agent when
      // inactive rows still share the same strategyId after rename/rebind.
      const byStrategy = new Map<string, { strategyId: string; agentId: string; label: string; isActive: boolean }>();
      const taken: Record<string, string> = {};
      (a?.items || []).forEach((x) => {
        const strategyId = (x.strategyId || '').trim();
        if (!strategyId) return;
        if (x.isActive && !taken[strategyId]) {
          taken[strategyId] = x.agentId;
        }
        const prev = byStrategy.get(strategyId);
        if (!prev || (x.isActive && !prev.isActive)) {
          byStrategy.set(strategyId, {
            strategyId,
            agentId: x.agentId,
            label: strategyId,
            isActive: !!x.isActive,
          });
        }
      });
      setTakenActiveBy(taken);
      setStrategyOptions(
        Array.from(byStrategy.values()).map(({ strategyId, agentId, label }) => ({
          strategyId,
          agentId,
          label,
        })),
      );
    } catch {
      /* keep previous options */
    }
  }, []);

  useEffect(() => {
    stAgentsConfig()
      .then((cfg) => setCatalogStrategyIds((cfg?.strategyIds || []).map((id) => id.trim()).filter(Boolean)))
      .catch(() => setCatalogStrategyIds([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    stAvatarDefaults()
      .then((d) => setUserDefaultAvatars((d?.userAvatars || []).filter(Boolean)))
      .catch(() => setUserDefaultAvatars([]));
  }, []);

  useEffect(() => {
    loadStrategyOptions();
  }, [loadStrategyOptions]);

  const createAgent = async () => {
    if (!createForm.name.trim()) {
      notify(t('module.socialTrading.agentNeedName'), { type: 'warning' });
      return;
    }
    if (!catalogStrategyIds.includes(createForm.strategyId.trim())) {
      notify(t('module.socialTrading.agentNeedStrategy'), { type: 'warning' });
      return;
    }
    const createSid = createForm.strategyId.trim();
    if (takenActiveBy[createSid]) {
      notify(t('module.socialTrading.agentDuplicateStrategy'), { type: 'warning' });
      return;
    }
    const strategyName = (createForm.strategyName.trim() || createForm.strategyId).trim();
    if (!isLangPackComplete(syncZh(createForm.strategyNameLang, strategyName), false)) {
      notify(t('module.socialTrading.i18nPackIncomplete'), { type: 'warning' });
      return;
    }
    if (!isLangPackComplete(syncZh(createForm.descriptionLang, createForm.description), true)) {
      notify(t('module.socialTrading.i18nPackIncomplete'), { type: 'warning' });
      return;
    }
    try {
      const tags = createForm.productTags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await stCreateAgent({
        name: createForm.name.trim(),
        strategyId: createForm.strategyId.trim(),
        strategyName,
        description: createForm.description.trim() || undefined,
        strategyNameLang: syncZh(createForm.strategyNameLang, strategyName),
        descriptionLang: syncZh(createForm.descriptionLang, createForm.description.trim()),
        avatar: createForm.avatar.trim() || undefined,
        symbol: createForm.symbol.trim() || undefined,
        ohlcv: createForm.ohlcv.trim() || undefined,
        version: createForm.version.trim() || undefined,
        riskLevel: createForm.riskLevel.trim() || undefined,
        productTags: tags.length ? tags : undefined,
        winRate: percentStrToRatio(createForm.winRate),
        sharpe: numOrUndef(createForm.sharpe),
        maxDrawdown: percentStrToRatio(createForm.maxDrawdown),
        pnl30d: percentStrToRatio(createForm.pnl30d),
        sevenDayReturn: percentStrToRatio(createForm.sevenDayReturn),
        fromBeginning: percentStrToRatio(createForm.fromBeginning),
      });
      setCreateOpen(false);
      setCreateForm(emptyAgentForm());
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      if (page !== 0) setPage(0);
      else load();
      loadStrategyOptions();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const openEdit = (a: StAgent) => {
    setEditAgent(a);
    setEditForm(agentFormFromAgent(a));
  };

  const saveEdit = async () => {
    if (!editAgent) return;
    if (!editForm.name.trim()) {
      notify(t('module.socialTrading.agentNeedName'), { type: 'warning' });
      return;
    }
    if (!catalogStrategyIds.includes(editForm.strategyId.trim())) {
      notify(t('module.socialTrading.agentNeedStrategy'), { type: 'warning' });
      return;
    }
    const editSid = editForm.strategyId.trim();
    const takenBy = takenActiveBy[editSid];
    if (takenBy && takenBy !== editAgent.agentId && editForm.isActive) {
      notify(t('module.socialTrading.agentDuplicateStrategy'), { type: 'warning' });
      return;
    }
    const strategyName = (editForm.strategyName.trim() || editForm.strategyId).trim();
    if (
      editForm.strategyNameLangDirty &&
      !isLangPackComplete(syncZh(editForm.strategyNameLang, strategyName), false)
    ) {
      notify(t('module.socialTrading.i18nPackIncomplete'), { type: 'warning' });
      return;
    }
    if (
      editForm.descriptionLangDirty &&
      !isLangPackComplete(syncZh(editForm.descriptionLang, editForm.description), true)
    ) {
      notify(t('module.socialTrading.i18nPackIncomplete'), { type: 'warning' });
      return;
    }
    try {
      const tags = editForm.productTags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const updated = await stUpdateAgent({
        agentId: editAgent.agentId,
        name: editForm.name.trim(),
        strategyId: editForm.strategyId.trim(),
        strategyName,
        description: editForm.description.trim() || undefined,
        avatar: editForm.avatar.trim() || undefined,
        isActive: editForm.isActive,
        symbol: editForm.symbol.trim() || undefined,
        ohlcv: editForm.ohlcv.trim() || undefined,
        version: editForm.version.trim() || undefined,
        riskLevel: editForm.riskLevel.trim() || undefined,
        productTags: tags.length ? tags : undefined,
        winRate: percentStrToRatio(editForm.winRate),
        sharpe: numOrUndef(editForm.sharpe),
        maxDrawdown: percentStrToRatio(editForm.maxDrawdown),
        pnl30d: percentStrToRatio(editForm.pnl30d),
        sevenDayReturn: percentStrToRatio(editForm.sevenDayReturn),
        fromBeginning: percentStrToRatio(editForm.fromBeginning),
        restoreDefaultProfile: editForm.restoreDefaultProfile || undefined,
        ...(editForm.strategyNameLangDirty
          ? { strategyNameLang: syncZh(editForm.strategyNameLang, strategyName) }
          : {}),
        ...(editForm.descriptionLangDirty
          ? { descriptionLang: syncZh(editForm.descriptionLang, editForm.description.trim()) }
          : {}),
      });
      const next = updated?.agent;
      if (next) {
        setAgents((prev) => prev.map((x) => (x.agentId === next.agentId ? next : x)));
        if (cardAgent?.agentId === next.agentId) {
          setCardAgent(next);
        }
      }
      setEditAgent(null);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      load();
      loadStrategyOptions();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const openCard = async (a?: StAgent) => {
    let hours = '24';
    try {
      const cfg = await stAgentsConfig();
      if (cfg?.cardValidHours != null) {
        hours = String(cfg.cardValidHours);
      }
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'warning' });
    }
    if (a) {
      setCardAgent(a);
      setCardForm({ ...cardFormFromAgent(a), cardValidHours: hours });
    } else {
      setCardAgent(null);
      setCardForm({ ...emptyCardForm(), cardValidHours: hours });
    }
    setCardOpen(true);
  };

  const sendCard = async () => {
    const strategyId = cardForm.strategyId.trim();
    if (!strategyId) {
      notify(t('module.socialTrading.cardNeedStrategy'), { type: 'warning' });
      return;
    }
    if (!cardForm.title.trim() || !cardForm.entry.trim() || !cardForm.symbol.trim() || !cardForm.ohlcv.trim()) {
      notify(t('module.socialTrading.cardNeedFields'), { type: 'warning' });
      return;
    }
    if (!cardForm.stopLoss.trim() || !cardForm.takeProfit.trim()) {
      notify(t('module.socialTrading.cardNeedFields'), { type: 'warning' });
      return;
    }
    const missingTitle = validateReasoningForm(cardForm.reasoning);
    if (missingTitle) {
      notify(t('module.socialTrading.cardNeedReasoning'), { type: 'warning' });
      return;
    }
    const reasoning = cardForm.reasoning
      .filter((e) => e.title.trim() && e.detailCn.trim() && e.detailEn.trim())
      .map((e) => ({
        title: e.title.trim(),
        detail_cn: e.detailCn.trim(),
        detail_en: e.detailEn.trim(),
      }));
    const tags = cardForm.productTags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!tags.length) {
      notify(t('module.socialTrading.cardNeedFields'), { type: 'warning' });
      return;
    }

    const now = Date.now();
    const hours = Number(cardForm.cardValidHours) || 24;
    const validMs = hours * 60 * 60 * 1000;
    const curve = equityCurveFromAgent(cardAgent);

    const customData = JSON.stringify({
      signalId: `admin-${now}`,
      title: cardForm.title.trim(),
      symbol: cardForm.symbol.trim(),
      ohlcv: cardForm.ohlcv.trim(),
      side: cardForm.side,
      entry: cardForm.entry.trim(),
      stopLoss: cardForm.stopLoss.trim(),
      takeProfit: cardForm.takeProfit.trim(),
      leverage: Number(cardForm.leverage) || 1,
      confidence: Number(cardForm.confidence) || 0,
      strategyId,
      source: cardForm.source.trim() || 'XBIT',
      version: cardForm.version.trim(),
      productTags: tags,
      riskLevel: cardForm.riskLevel.trim(),
      winRate: percentStrToRatio(cardForm.winRate) ?? 0,
      sharpe: Number(cardForm.sharpe),
      maxDrawdown: percentStrToRatio(cardForm.maxDrawdown) ?? 0,
      pnl30d: percentStrToRatio(cardForm.pnl30d) ?? 0,
      sevenDayReturn: percentStrToRatio(cardForm.sevenDayReturn) ?? 0,
      fromBeginning: percentStrToRatio(cardForm.fromBeginning) ?? 0,
      equityCurve: curve,
      reasoning,
      createdAt: now,
      validUntil: now + validMs,
    });

    try {
      await stSendRoomMessage({
        strategyId,
        extension: EXT_AGENT_SIGNAL,
        customData,
      });
      setCardOpen(false);
      notify(t('module.socialTrading.sendCardOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const percentHelper = (raw: string): string => {
    const n = numOrUndef(raw);
    if (n === undefined) {
      return raw.trim()
        ? t('module.socialTrading.metricsPercentInvalid')
        : t('module.socialTrading.metricsPercentPreview', { value: '—' });
    }
    return t('module.socialTrading.metricsPercentPreview', { value: String(n) });
  };

  const percentField = (label: string, value: string, onChange: (v: string) => void) => (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      InputProps={{
        endAdornment: <InputAdornment position="end">%</InputAdornment>,
      }}
      helperText={percentHelper(value)}
    />
  );

  const agentFormFields = (
    form: AgentForm,
    setForm: (f: AgentForm | ((prev: AgentForm) => AgentForm)) => void,
    opts?: { showActive?: boolean; showRestore?: boolean; exceptAgentId?: string },
  ) => {
    const strategyChoices = (
      form.strategyId && !catalogStrategyIds.includes(form.strategyId)
        ? [...catalogStrategyIds, form.strategyId]
        : catalogStrategyIds
    ).filter((id) => {
      const owner = takenActiveBy[id];
      if (!owner) return true;
      if (opts?.exceptAgentId && owner === opts.exceptAgentId) return true;
      if (form.strategyId === id) return true;
      return false;
    });
    return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {form.i18nStale && (
        <Alert severity="warning">{t('module.socialTrading.i18nStaleHint')}</Alert>
      )}
      <TextField size="small" label="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <TextField
        select
        size="small"
        label="strategyId"
        required
        value={form.strategyId}
        onChange={(e) => {
          const strategyId = e.target.value;
          setForm({
            ...form,
            strategyId,
            strategyName: form.strategyName.trim() ? form.strategyName : strategyId,
          });
        }}
        helperText={t('module.socialTrading.strategyHint')}
      >
        <MenuItem value="" disabled>
          {t('module.socialTrading.strategySelectPlaceholder')}
        </MenuItem>
        {strategyChoices.map((id) => (
          <MenuItem key={id} value={id}>
            {id}
          </MenuItem>
        ))}
      </TextField>
      <I18nFieldBlock
        fieldKey="strategyName"
        label="strategyName"
        required
        primary={form.strategyName}
        onPrimaryChange={(strategyName) => setForm({ ...form, strategyName })}
        pack={form.strategyNameLang}
        onPackChange={(strategyNameLang) =>
          setForm({ ...form, strategyNameLang, strategyNameLangDirty: true, i18nStale: false })
        }
        onPrimaryEditedAfterFill={() => setForm((prev) => ({ ...prev, i18nStale: true }))}
      />
      <I18nFieldBlock
        fieldKey="description"
        label="description"
        primary={form.description}
        onPrimaryChange={(description) => setForm({ ...form, description })}
        pack={form.descriptionLang}
        onPackChange={(descriptionLang) =>
          setForm({ ...form, descriptionLang, descriptionLangDirty: true, i18nStale: false })
        }
        onPrimaryEditedAfterFill={() => setForm((prev) => ({ ...prev, i18nStale: true }))}
        multiline
        rows={3}
        allowEmptyFill
      />
      <SocialTradingAvatarField
        value={form.avatar}
        onChange={(url) => setForm({ ...form, avatar: url })}
        defaultUrls={userDefaultAvatars}
        uploadPath="/upload"
      />
      <Typography variant="caption" color="text.secondary">
        {t('module.socialTrading.defaultProfileHint')}
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField size="small" label="symbol" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} fullWidth />
        <TextField size="small" label="ohlcv" value={form.ohlcv} onChange={(e) => setForm({ ...form, ohlcv: e.target.value })} fullWidth />
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField size="small" label="version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} fullWidth />
        <TextField size="small" label="riskLevel" value={form.riskLevel} onChange={(e) => setForm({ ...form, riskLevel: e.target.value })} fullWidth />
      </Stack>
      <TextField
        size="small"
        label="productTags"
        value={form.productTags}
        onChange={(e) => setForm({ ...form, productTags: e.target.value })}
      />
      <Typography variant="caption" color="text.secondary">
        {t('module.socialTrading.metricsRatioHint')}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {percentField('winRate', form.winRate, (v) => setForm({ ...form, winRate: v }))}
        <TextField size="small" label="sharpe" value={form.sharpe} onChange={(e) => setForm({ ...form, sharpe: e.target.value })} fullWidth />
        {percentField('maxDrawdown', form.maxDrawdown, (v) => setForm({ ...form, maxDrawdown: v }))}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {percentField('pnl30d', form.pnl30d, (v) => setForm({ ...form, pnl30d: v }))}
        {percentField('sevenDayReturn', form.sevenDayReturn, (v) => setForm({ ...form, sevenDayReturn: v }))}
        {percentField('fromBeginning', form.fromBeginning, (v) => setForm({ ...form, fromBeginning: v }))}
      </Stack>
      {opts?.showActive && (
        <FormControlLabel
          control={<Checkbox checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
          label="isActive"
        />
      )}
      {opts?.showRestore && (
        <FormControlLabel
          control={
            <Checkbox
              checked={form.restoreDefaultProfile}
              onChange={(e) => setForm({ ...form, restoreDefaultProfile: e.target.checked })}
            />
          }
          label={t('module.socialTrading.restoreDefaultProfile')}
        />
      )}
    </Stack>
    );
  };

  return (
    <PermissionGuard resource="st-agents" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.agents')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.agents') },
          ]}
          action={
            <Button size="small" variant="outlined" onClick={load}>
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />

        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
              <Typography variant="h6" sx={{ flex: 1 }}>
                Agents
              </Typography>
              {/* POST /social-trading/integration/push/room-message requires st-rooms:create */}
              <PermissionButton resource="st-rooms" action="create">
                <Button variant="outlined" onClick={() => openCard()}>
                  {t('module.socialTrading.sendCard')}
                </Button>
              </PermissionButton>
              {/* POST /social-trading/integration/agents requires st-agents:create OR st-rooms:create */}
              <PermissionGuard
                anyOf={[
                  { resource: 'st-agents', action: 'create' },
                  { resource: 'st-rooms', action: 'create' },
                ]}
                fallback={null}
                loadingFallback={null}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    setCreateForm(emptyAgentForm());
                    setCreateOpen(true);
                  }}
                >
                  {t('module.socialTrading.createAgent')}
                </Button>
              </PermissionGuard>
            </Stack>
            <StScrollTable>
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell>
                  <TableCell>agentId</TableCell>
                  <TableCell>botAccountId</TableCell>
                  <TableCell>strategyId</TableCell>
                  <TableCell>strategyName</TableCell>
                  <TableCell>{t('module.socialTrading.colProfileSource')}</TableCell>
                  <TableCell>latestAgentSignalId</TableCell>
                  <TableCell>{t('module.socialTrading.colBoundRooms')}</TableCell>
                  <TableCell>{t('module.socialTrading.colActive')}</TableCell>
                  <TableCell align="right">{t('module.socialTrading.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agents.map((a) => (
                  <TableRow key={a.agentId}>
                    <TableCell>{stText(a.name)}</TableCell>
                    <TableCell><StIdCell value={a.agentId} /></TableCell>
                    <TableCell><StIdCell value={a.botAccountId} /></TableCell>
                    <TableCell>{stText(a.strategyId)}</TableCell>
                    <TableCell>{stText(a.strategyName)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={profileSourceLabel(a.profileSource, t)}
                        color={a.profileSource === 'LATEST_SIGNAL' ? 'info' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <StIdCell value={a.latestAgentSignalId} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', verticalAlign: 'top' }}>
                      <BoundRoomsCell agent={a} />
                    </TableCell>
                    <TableCell>
                      <StBooleanChip value={a.isActive} />
                    </TableCell>
                    <TableCell align="right">
                      <StActionRow>
                        {/* POST /social-trading/integration/agents/update requires st-agents:update */}
                        <PermissionButton resource="st-agents" action="update">
                          <StActionButton onClick={() => openEdit(a)}>
                            {t('module.socialTrading.editAgent')}
                          </StActionButton>
                        </PermissionButton>
                        {/* POST /social-trading/integration/push/room-message requires st-rooms:create */}
                        <PermissionButton resource="st-rooms" action="create">
                          <StActionButton onClick={() => openCard(a)}>
                            {t('module.socialTrading.sendCard')}
                          </StActionButton>
                        </PermissionButton>
                      </StActionRow>
                    </TableCell>
                  </TableRow>
                ))}
                {!agents.length && (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </StScrollTable>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </CardContent>
        </Card>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('module.socialTrading.createAgent')}</DialogTitle>
          <DialogContent>{agentFormFields(createForm, setCreateForm)}</DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/integration/agents requires st-agents:create OR st-rooms:create */}
            <PermissionGuard
              anyOf={[
                { resource: 'st-agents', action: 'create' },
                { resource: 'st-rooms', action: 'create' },
              ]}
              fallback={null}
              loadingFallback={null}
            >
              <Button variant="contained" onClick={createAgent}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionGuard>
          </DialogActions>
        </Dialog>

        <Dialog open={!!editAgent} onClose={() => setEditAgent(null)} fullWidth maxWidth="sm">
          <DialogTitle>{t('module.socialTrading.editAgent')}</DialogTitle>
          <DialogContent>
            {editAgent ? (
              <Stack spacing={1} sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2">
                  {t('module.socialTrading.colProfileSource')}: {profileSourceLabel(editAgent.profileSource, t)}
                </Typography>
                {editAgent.latestAgentSignalId ? (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    latestAgentSignalId: {editAgent.latestAgentSignalId}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
            {agentFormFields(editForm, setEditForm, {
              showActive: true,
              showRestore: editAgent?.profileSource === 'LATEST_SIGNAL',
              exceptAgentId: editAgent?.agentId,
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditAgent(null)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/integration/agents/update requires st-agents:update */}
            <PermissionButton resource="st-agents" action="update">
              <Button variant="contained" onClick={saveEdit}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <Dialog open={cardOpen} onClose={() => setCardOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{t('module.socialTrading.sendCard')}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('module.socialTrading.cardTargetHint')}
              </Typography>
              <TextField
                select
                size="small"
                label="strategyId"
                value={cardForm.strategyId}
                onChange={(e) => {
                  const sid = e.target.value;
                  const hit = strategyOptions.find((o) => o.strategyId === sid);
                  const agent = agents.find((a) => a.agentId === hit?.agentId);
                  if (agent) {
                    setCardAgent(agent);
                    setCardForm({ ...cardFormFromAgent(agent), strategyId: sid });
                  } else {
                    setCardForm({ ...cardForm, strategyId: sid, agentId: hit?.agentId || cardForm.agentId });
                  }
                }}
              >
                <MenuItem value="">-</MenuItem>
                {strategyOptions.map((o) => (
                  <MenuItem key={o.strategyId} value={o.strategyId}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="strategyId (manual)"
                value={cardForm.strategyId}
                onChange={(e) => setCardForm({ ...cardForm, strategyId: e.target.value })}
                required
              />
              <TextField
                size="small"
                label="agentId (reference)"
                value={cardForm.agentId}
                InputProps={{ readOnly: true }}
                helperText={t('module.socialTrading.cardAgentIdHint')}
              />
              <TextField
                size="small"
                label="title"
                required
                value={cardForm.title}
                onChange={(e) => setCardForm({ ...cardForm, title: e.target.value })}
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="symbol"
                  required
                  value={cardForm.symbol}
                  onChange={(e) => setCardForm({ ...cardForm, symbol: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="ohlcv"
                  required
                  value={cardForm.ohlcv}
                  onChange={(e) => setCardForm({ ...cardForm, ohlcv: e.target.value })}
                  fullWidth
                />
              </Stack>
              <TextField
                select
                size="small"
                label="side"
                value={cardForm.side}
                onChange={(e) => setCardForm({ ...cardForm, side: e.target.value as 'long' | 'short' })}
              >
                <MenuItem value="long">long</MenuItem>
                <MenuItem value="short">short</MenuItem>
              </TextField>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="entry"
                  required
                  value={cardForm.entry}
                  onChange={(e) => setCardForm({ ...cardForm, entry: e.target.value })}
                />
                <TextField
                  size="small"
                  label="stopLoss"
                  required
                  value={cardForm.stopLoss}
                  onChange={(e) => setCardForm({ ...cardForm, stopLoss: e.target.value })}
                />
                <TextField
                  size="small"
                  label="takeProfit"
                  required
                  value={cardForm.takeProfit}
                  onChange={(e) => setCardForm({ ...cardForm, takeProfit: e.target.value })}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="leverage"
                  value={cardForm.leverage}
                  onChange={(e) => setCardForm({ ...cardForm, leverage: e.target.value })}
                />
                <TextField
                  size="small"
                  label="confidence"
                  value={cardForm.confidence}
                  onChange={(e) => setCardForm({ ...cardForm, confidence: e.target.value })}
                />
                <TextField
                  size="small"
                  label={t('module.socialTrading.cardValidHours')}
                  value={cardForm.cardValidHours}
                  InputProps={{ readOnly: true }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="version"
                  value={cardForm.version}
                  onChange={(e) => setCardForm({ ...cardForm, version: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="riskLevel"
                  value={cardForm.riskLevel}
                  onChange={(e) => setCardForm({ ...cardForm, riskLevel: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="source"
                  value={cardForm.source}
                  onChange={(e) => setCardForm({ ...cardForm, source: e.target.value })}
                  fullWidth
                />
              </Stack>
              <TextField
                size="small"
                label="productTags"
                value={cardForm.productTags}
                onChange={(e) => setCardForm({ ...cardForm, productTags: e.target.value })}
              />
              <Typography variant="caption" color="text.secondary">
                {t('module.socialTrading.metricsRatioHint')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {percentField('winRate', cardForm.winRate, (v) => setCardForm({ ...cardForm, winRate: v }))}
                <TextField
                  size="small"
                  label="sharpe"
                  value={cardForm.sharpe}
                  onChange={(e) => setCardForm({ ...cardForm, sharpe: e.target.value })}
                  fullWidth
                />
                {percentField('maxDrawdown', cardForm.maxDrawdown, (v) => setCardForm({ ...cardForm, maxDrawdown: v }))}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {percentField('pnl30d', cardForm.pnl30d, (v) => setCardForm({ ...cardForm, pnl30d: v }))}
                {percentField('sevenDayReturn', cardForm.sevenDayReturn, (v) =>
                  setCardForm({ ...cardForm, sevenDayReturn: v }),
                )}
                {percentField('fromBeginning', cardForm.fromBeginning, (v) =>
                  setCardForm({ ...cardForm, fromBeginning: v }),
                )}
              </Stack>
              <Typography variant="subtitle2">{t('module.socialTrading.reasoningTitle')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('module.socialTrading.reasoningHint')}
              </Typography>
              {cardForm.reasoning.map((entry, idx) => {
                const labelCn =
                  REASONING_TITLE_CN[entry.title as (typeof REQUIRED_REASONING_TITLES)[number]] ||
                  entry.title;
                return (
                  <Stack key={`${entry.title}-${idx}`} spacing={1} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {entry.title}
                      {labelCn !== entry.title ? ` · ${labelCn}` : ''}
                    </Typography>
                    <TextField
                      size="small"
                      label={t('module.socialTrading.reasoningDetailCn')}
                      multiline
                      minRows={2}
                      value={entry.detailCn}
                      onChange={(e) => {
                        const next = [...cardForm.reasoning];
                        next[idx] = { ...next[idx], detailCn: e.target.value };
                        setCardForm({ ...cardForm, reasoning: next });
                      }}
                    />
                    <TextField
                      size="small"
                      label={t('module.socialTrading.reasoningDetailEn')}
                      multiline
                      minRows={2}
                      value={entry.detailEn}
                      onChange={(e) => {
                        const next = [...cardForm.reasoning];
                        next[idx] = { ...next[idx], detailEn: e.target.value };
                        setCardForm({ ...cardForm, reasoning: next });
                      }}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCardOpen(false)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/integration/push/room-message requires st-rooms:create */}
            <PermissionButton resource="st-rooms" action="create">
              <Button variant="contained" onClick={sendCard}>
                {t('module.socialTrading.send')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

      </DashboardContent>
    </PermissionGuard>
  );
}
